<script setup lang="ts">
</script>

<template>
  <div>
    <main class="mx-auto max-w-3xl px4 lg:max-w-[88rem] md:max-w-4xl lg:px6 sm:px4 sm:px6">
      <!-- Product -->
      <div class="mb6">
        <div class="lg:(grid grid-cols-7 grid-rows-1 gap-x-8 gap-y-10) xl:(gap-x-10) sm:pt8">
          <!-- Gallery skeleton -->
          <div class="lg:(col-span-4 row-end-1) -mx4 md:mx0">
            <div class="relative">
              <!-- Main image with overlays -->
              <NSkeleton shape="rect" class="aspect-1/1 w-full" wave />
              <!-- Overlay badges (shipping & savings) -->
              <div class="absolute left-2 top-2 z20">
                <div class="flex flex-col items-start space-y-2">
                  <NSkeleton class="h-8 w-32 rounded-md" shape="rect" />
                  <NSkeleton class="h-8 w-36 rounded-md" shape="rect" />
                </div>
              </div>
              <!-- Zoom button overlay -->
              <div class="absolute start-0 bottom-0 z-20 px4 pb3.75 pt4">
                <NSkeleton class="h12 w12 rounded-full" shape="circle" />
              </div>
            </div>

            <!-- Thumbnail images - hidden on mobile -->
            <div class="grid-cols-4 mt-4 gap-2 px-0 hidden md:grid">
              <NSkeleton v-for="i in 4" :key="i" shape="rect" class="aspect-1/1 w-full rounded-md" />
            </div>
            <!-- Product Reviews skeleton (desktop only) -->
            <div class="mt8 hidden lg:block">
              <NSkeleton class="h-64 w-full rounded-lg" shape="rect" />
            </div>
          </div>

          <!-- Product details skeleton -->
          <div class="mx-auto mt2 max-w-2xl w-full lg:(col-span-3 row-span-2 row-end-2 mt0 max-w-none)">
            <div class="flex-col px0 space-y-1 lg:px0 md:px4 sm:px6 md:space-y-2">
              <!-- Title -->
              <div>
                <!-- Breadcrumbs skeleton -->
                <div class="mb-2 hidden md:block">
                  <NSkeleton class="h-4 w-3/4" />
                </div>

                <NSkeleton class="h-8 w-full md:text-base" />

                <!-- Rating skeleton -->
                <div class="flex items-center justify-between gap-x-1 gap-y-1 py-1 md:(justify-start gap-x-2)">
                  <div class="flex gap-x-1 rd-md py-0.5 sm:(flex-row from-success/20 to-transparent bg-gradient-to-r pl-1) md:gap-x-2">
                    <!-- Verified Purchase -->
                    <div class="flex shrink-0 items-center">
                      <NSkeleton shape="circle" class="mr-0.5 size-5" />
                      <NSkeleton class="h-5 w-32" />
                    </div>
                    <!-- Rating -->
                    <div class="flex shrink-0 items-center">
                      <NSkeleton class="h-5 w-28" />
                    </div>
                  </div>
                  <!-- Dot Separator -->
                  <div class="mx-1 text-sm text-slate-400 hidden sm:block">
                    •
                  </div>
                  <!-- Sold Counter -->
                  <div class="items-center hidden md:flex">
                    <NSkeleton class="h-5 w-36" />
                    <NSkeleton shape="circle" class="ml-1 size-4" />
                  </div>
                </div>
              </div>

              <!-- Price skeleton -->
              <div>
                <div class="flex flex items-center justify-between md:(justify-start space-x-2)">
                  <div class="flex items-center justify-start space-x-2">
                    <NSkeleton class="h-8 w-32 md:h-10" />
                    <NSkeleton class="h-6 w-24 md:h-8" />
                    <NSkeleton class="h-6 w-16 rounded-md" shape="rect" />
                  </div>
                </div>
              </div>

              <!-- In stock & shipping info skeleton -->
              <div class="flex items-center">
                <div class="flex items-center rounded py-1 pb-2">
                  <div class="flex flex-col">
                    <div class="flex items-center gap-2 md:gap-2.5">
                      <NSkeleton shape="circle" class="size-3" />
                      <NSkeleton class="h-5 w-56" />
                    </div>
                    <div class="mt-1">
                      <NSkeleton class="h-4 w-64" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- USP Highlights skeleton -->
              <div class="py-2">
                <div class="grid grid-cols-2 gap-2">
                  <NSkeleton v-for="i in 4" :key="i" class="h-12 w-full rounded-lg" shape="rect" />
                </div>
              </div>

              <!-- Product options skeleton -->
              <div data-testid="productForm">
                <div class="space-y-4">
                  <!-- Color option -->
                  <div class="mt4">
                    <div class="flex items-center justify-between">
                      <NSkeleton class="h-5 w-24" />
                    </div>
                    <div class="mt-2 flex flex-wrap gap2">
                      <NSkeleton v-for="i in 5" :key="i" shape="rect" class="size-15 rounded-md" />
                    </div>
                  </div>

                  <!-- Size option -->
                  <div class="mt4">
                    <div class="flex items-center justify-between">
                      <NSkeleton class="h-5 w-16" />
                      <NSkeleton class="h-5 w-28" />
                    </div>
                    <div class="mt-2 flex flex-wrap gap2">
                      <NSkeleton v-for="i in 8" :key="i" class="h-11 w-16 rounded-md" shape="rect" />
                    </div>
                  </div>
                </div>

                <!-- Add to cart button skeleton -->
                <NSkeleton class="mt-4 h-14 w-full rounded-md" shape="rect" wave />
                <!-- Order countdown skeleton -->
                <NSkeleton class="mt-1 h-6 w-full" />
              </div>

              <!-- Policies skeleton -->
              <div class="py-4">
                <div class="grid grid-cols-2 gap-3">
                  <NSkeleton v-for="i in 4" :key="i" class="h-16 w-full rounded-lg" shape="rect" />
                </div>
              </div>

              <!-- Mobile separator skeleton -->
              <div class="mb4 block bg-slate/30 pb4 shadow-inner -mx4 sm:hidden" />

              <!-- Accordion skeleton -->
              <div class="mt-4">
                <div class="space-y-1">
                  <NSkeleton v-for="i in 4" :key="i" class="h-14 w-full" />
                </div>
              </div>

              <!-- Product Reviews skeleton (mobile only) -->
              <div class="mt8 lg:hidden">
                <NSkeleton class="h-64 w-full" shape="rect" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Marketing Prize Banner skeleton -->
      <NSkeleton class="h72 w-full rounded-xl lg:h112 sm:h64" shape="rect" />

      <!-- Mobile separator -->
      <div class="my4 block bg-slate/30 pb4 shadow-inner -mx4 sm:hidden" />

      <!-- Satisfaction guarantee skeleton -->
      <div class="py-16 sm:py-24">
        <div class="mb-12 text-center">
          <NSkeleton class="mx-auto mb-8 h-32 w-32" shape="circle" />
          <NSkeleton class="mx-auto mb-4 h-10 w-96" />
          <NSkeleton class="mx-auto h-6 w-128" />
        </div>
        <div class="grid grid-cols-1 gap-8 lg:grid-cols-3 sm:grid-cols-2">
          <NSkeleton v-for="i in 3" :key="i" class="h-64 w-full rounded-3xl" shape="rect" />
        </div>
      </div>

      <!-- Customer support skeleton -->
      <div class="px4 py6 sm:mb8 lg:px8 sm:px6">
        <div class="mb-4 flex items-center justify-center">
          <NSkeleton shape="circle" class="mr-3 size-3" />
          <NSkeleton class="h-6 w-72" />
        </div>
        <NSkeleton class="mx-auto h-12 w-64 rounded-lg" shape="rect" />
      </div>
    </main>

    <!-- Related products skeleton -->
    <div class="mx-auto max-w-3xl px2 pb8 pt2 lg:(max-w-[88rem] px6) md:max-w-4xl sm:(px4 pb12 pt6)">
      <!-- Recently viewed section -->
      <div class="mb12">
        <NSkeleton class="mb-6 h-6 w-48" />
        <div class="grid grid-cols-2 gap-x-2 gap-y-4 sm:grid-cols-minmax-12rem">
          <ProductCardSkeleton v-for="i in 4" :key="i" />
        </div>
      </div>

      <!-- Related items section -->
      <div>
        <NSkeleton class="mb-6 h-6 w-48" />
        <div class="grid grid-cols-2 gap-x-2 gap-y-4 sm:grid-cols-minmax-12rem">
          <ProductCardSkeleton v-for="i in 8" :key="i" />
        </div>
      </div>
    </div>
  </div>
</template>
